import { LogIn, LogOut, Shield } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isUserAllowed } from '../config/allowedUsers';
import { useAuth } from '../hooks/useAuth';
import { isSupabaseConfigured } from '../lib/supabase';

interface AuthGuardProps {
    children: ReactNode;
    fallback?: ReactNode;
}

function LoginScreen({
    onSignInWithGoogle,
    onSignInWithPassword,
}: {
    onSignInWithGoogle: () => void;
    onSignInWithPassword: (email: string, password: string) => Promise<unknown>;
}) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isLocalDev = import.meta.env.DEV;

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || !password) return;

        setIsLoading(true);
        setError(null);

        try {
            await onSignInWithPassword(email.trim(), password);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid email or password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        Welcome to InboundX Preprod
                    </h2>
                    <p className="text-gray-600 mb-8">
                        Please sign in to access the testing environment
                    </p>

                    {isLocalDev && (
                        <>
                            <form onSubmit={handlePasswordSubmit} className="space-y-4 mb-6">
                                <Input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full"
                                />
                                <Input
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full"
                                />

                                {error && <p className="text-sm text-red-600">{error}</p>}

                                <Button
                                    type="submit"
                                    size="lg"
                                    className="w-full"
                                    disabled={isLoading || !email.trim() || !password}
                                >
                                    <LogIn className="mr-2 h-5 w-5" />
                                    {isLoading ? 'Signing in...' : 'Sign in'}
                                </Button>
                            </form>

                            <div className="relative mb-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-gray-500">or</span>
                                </div>
                            </div>
                        </>
                    )}

                    <Button
                        onClick={onSignInWithGoogle}
                        variant={isLocalDev ? 'outline' : 'default'}
                        size="lg"
                        className="w-full flex items-center justify-center gap-3"
                    >
                        <LogIn className="h-5 w-5" />
                        Sign in with Google
                    </Button>

                    <p className="mt-4 text-sm text-gray-500">
                        By signing in, you agree to our terms and conditions
                    </p>
                </div>
            </div>
        </div>
    );
}

export function AuthGuard({ children, fallback }: AuthGuardProps) {
    const { user, loading, signInWithGoogle, signInWithPassword, signOut } = useAuth();

    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
        // Show the app with a warning banner instead of blocking
        return (
            <>
                <div className="fixed top-0 left-0 right-0 bg-yellow-50 border-b border-yellow-200 p-3 z-[1000]">
                    <div className="flex items-center justify-center gap-2 text-sm">
                        <Shield className="h-4 w-4 text-yellow-600" />
                        <span className="text-yellow-800 font-medium">
                            Authentication not configured - Running in bypass mode
                        </span>
                    </div>
                </div>
                <div style={{ paddingTop: '52px' }}>{children}</div>
            </>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        if (fallback) {
            return <>{fallback}</>;
        }

        return (
            <LoginScreen
                onSignInWithGoogle={signInWithGoogle}
                onSignInWithPassword={signInWithPassword}
            />
        );
    }

    // Check if user is authorized
    if (!isUserAllowed(user.email || '')) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
                <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-2xl border border-red-100">
                    <div className="text-center">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Shield className="h-10 w-10 text-red-600" />
                        </div>

                        <h2 className="text-3xl font-bold text-gray-900 mb-3">Access Restricted</h2>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <p className="text-red-800 font-medium mb-2">
                                Your account is not authorized
                            </p>
                            <p className="text-red-700 text-sm">
                                <strong>{user.email}</strong> is not in the approved list for this
                                environment.
                            </p>
                        </div>

                        <p className="text-gray-500 text-sm mb-6">
                            Contact your administrator to get access, or sign in with an authorized
                            account.
                        </p>

                        <div className="space-y-3">
                            <Button
                                onClick={signOut}
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3"
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                Sign out and try different account
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}
